import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link as RouterLink } from 'react-router-dom';
import { IProcountorInvoicePreviewGroup } from '../ZentuvoInvoicingApi';
import {
    formatCurrency,
    getMasterRecordNote,
    IVisibleGroup,
    normalizeOrderRowId,
    SortKey
} from '../procountorInvoiceHelpers';
import InvoiceRow from './InvoiceRow';

interface IInvoiceGroupAccordionProps {
    visibleGroup: IVisibleGroup;
    isExpanded: boolean;
    selectedRowIds: number[];
    sortKey: SortKey;
    sortDirection: 'asc' | 'desc';
    canEditBrokerTree: boolean;
    generatedProcountorIds: { [groupId: string]: string };
    isGeneratingProcountorId: (groupId: string) => boolean;
    onToggleExpansion: (groupId: string) => void;
    onToggleRow: (orderRowId: number | string) => void;
    onToggleGroupRows: (orderRowIds: Array<number | string>) => void;
    onRowStatusChange: (orderRowId: number | string, invoiceStatusValue: number) => void;
    onSortChange: (sortKey: SortKey) => void;
    onEditAgent: (name: string) => void;
    onEditFirm: (name: string) => void;
    onGenerateProcountorId: (groupId: string) => void;
    onCopyProcountorId: (groupId: string) => void;
    onCopyAssignmentPhrase: (group: IProcountorInvoicePreviewGroup) => void;
}

const InvoiceGroupAccordion: React.FC<IInvoiceGroupAccordionProps> = ({
    visibleGroup,
    isExpanded,
    selectedRowIds,
    sortKey,
    sortDirection,
    canEditBrokerTree,
    generatedProcountorIds,
    isGeneratingProcountorId,
    onToggleExpansion,
    onToggleRow,
    onToggleGroupRows,
    onRowStatusChange,
    onSortChange,
    onEditAgent,
    onEditFirm,
    onGenerateProcountorId,
    onCopyProcountorId,
    onCopyAssignmentPhrase
}) => {
    const group = visibleGroup.group;
    const masterRecordNote = getMasterRecordNote(group);
    const groupRowIds = visibleGroup.rows.map((row) => normalizeOrderRowId(row.OrderRowId));
    const groupAllSelected = groupRowIds.length > 0 && groupRowIds.every((rowId) => selectedRowIds.indexOf(rowId) >= 0);
    const groupSomeSelected = groupRowIds.some((rowId) => selectedRowIds.indexOf(rowId) >= 0);
    const groupSelectedCount = groupRowIds.filter((rowId) => selectedRowIds.indexOf(rowId) >= 0).length;

    return (
        <Accordion
            id={`zentuvo-group-${group.GroupId}`}
            expanded={isExpanded}
            onChange={() => onToggleExpansion(group.GroupId)}
            disableGutters
            TransitionProps={{ unmountOnExit: true }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }} sx={{ width: '100%' }}>
                    <Checkbox
                        checked={groupAllSelected}
                        indeterminate={!groupAllSelected && groupSomeSelected}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onToggleGroupRows(groupRowIds);
                        }}
                        onFocus={(event) => event.stopPropagation()}
                    />
                    <Chip
                        label={group.HasValidProcountorId ? 'Valid' : 'Missing ProcountorId'}
                        size="small"
                        color={group.HasValidProcountorId ? 'success' : 'warning'}
                    />
                    <Typography variant="body2" sx={{ userSelect: 'text' }} onMouseDown={(e) => e.stopPropagation()}>
                        <RouterLink
                            to="#"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (canEditBrokerTree) {
                                    onEditAgent(group.InvoiceReceiver || group.InvoiceEmail || '');
                                }
                            }}
                            target={undefined}
                            rel={undefined}
                        >
                            <strong>{group.InvoiceReceiver || group.InvoiceEmail || 'No billing recipient'}</strong>
                        </RouterLink>

                        {group.BrokerFirm && (
                            <Typography
                                component="div"
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    userSelect: 'text',
                                    marginTop: 0.5,
                                    cursor: canEditBrokerTree ? 'pointer' : 'default'
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <RouterLink
                                    to="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (canEditBrokerTree) {
                                            onEditFirm(group.BrokerFirm);
                                        }
                                    }}
                                    target={undefined}
                                    rel={undefined}
                                >
                                    {group.BrokerFirm}
                                </RouterLink>
                            </Typography>
                        )}
                    </Typography>
                    {group.ProcountorId ? (
                        <Chip
                            label={group.ProcountorId}
                            size="small"
                            variant="outlined"
                            sx={{ userSelect: 'text' }}
                            onMouseDown={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={0.1}
                            alignItems={{ sm: 'center' }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {!generatedProcountorIds[group.GroupId] && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    disabled={isGeneratingProcountorId(group.GroupId)}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onGenerateProcountorId(group.GroupId);
                                    }}
                                >
                                    {isGeneratingProcountorId(group.GroupId) ? 'Generating...' : 'Generate ProcountorId'}
                                </Button>
                            )}
                            {generatedProcountorIds[group.GroupId] && (
                                <>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            userSelect: 'text',
                                            fontFamily: 'monospace',
                                            fontWeight: 700
                                        }}
                                    >
                                        {generatedProcountorIds[group.GroupId]}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        aria-label="Copy ProcountorId to Clipboard"
                                        title="Copy ProcountorId to Clipboard"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            onCopyProcountorId(group.GroupId);
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                        </Stack>
                    )}
                    <Chip
                        label={`Total: ${
                            groupSelectedCount > 0
                                ? formatCurrency(
                                      visibleGroup.rows.reduce(
                                          (sum, row) =>
                                              selectedRowIds.indexOf(normalizeOrderRowId(row.OrderRowId)) >= 0
                                                  ? sum + row.TotalAmount
                                                  : sum,
                                          0
                                      )
                                  )
                                : formatCurrency(visibleGroup.rows.reduce((sum, row) => sum + row.TotalAmount, 0))
                        }`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ '.MuiChip-label': { userSelect: 'text' } }}
                    />
                    <Chip
                        label={`Rows: ${visibleGroup.rows.length}/${group.RowCount}${
                            groupSelectedCount > 0 ? ` (Selected: ${groupSelectedCount})` : ''
                        }`}
                        size="small"
                    />
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {isExpanded && (
                    <Stack spacing={2}>
                        <Paper variant="outlined" sx={{ padding: 2, backgroundColor: '#fafafa' }}>
                            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between">
                                <Box>
                                    <Typography variant="body2">Receiver: {group.InvoiceReceiver || '-'}</Typography>
                                    <Typography variant="body2">Email: {group.InvoiceEmail || '-'}</Typography>
                                    <Typography variant="body2">
                                        Address:{' '}
                                        {[group.InvoiceAddress, group.InvoiceZip, group.InvoiceCity]
                                            .filter((value) => !!value)
                                            .join(', ') || '-'}
                                    </Typography>
                                    {(generatedProcountorIds[group.GroupId] || group.ProcountorId) && (
                                        <Button
                                            size="small"
                                            variant="text"
                                            startIcon={<ContentCopyIcon fontSize="small" />}
                                            sx={{ paddingLeft: 0, marginTop: 0.5 }}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                onCopyAssignmentPhrase(group);
                                            }}
                                        >
                                            Copy assignment phrase
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2">CSV:</Typography>
                                    {group.MasterCsvLine ? (
                                        <Box
                                            component="pre"
                                            sx={{
                                                margin: 0,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                fontFamily: 'monospace',
                                                fontSize: 12
                                            }}
                                        >
                                            {group.MasterCsvLine}
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color={masterRecordNote.isWarning ? 'warning.main' : 'text.secondary'}
                                        >
                                            {masterRecordNote.text}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </Paper>

                        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">Select</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortKey === 'invoiceNumber'}
                                                direction={sortDirection}
                                                onClick={() => onSortChange('invoiceNumber')}
                                            >
                                                Invoice
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortKey === 'createdAt'}
                                                direction={sortDirection}
                                                onClick={() => onSortChange('createdAt')}
                                            >
                                                Created
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 260 }}>
                                            <TableSortLabel
                                                active={sortKey === 'productName'}
                                                direction={sortDirection}
                                                onClick={() => onSortChange('productName')}
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                Product / Comment
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">Qty</TableCell>
                                        <TableCell align="right">Price</TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={sortKey === 'totalAmount'}
                                                direction={sortDirection}
                                                onClick={() => onSortChange('totalAmount')}
                                            >
                                                Total
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Property</TableCell>
                                        <TableCell>Validation</TableCell>
                                        <TableCell>CSV detail line</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleGroup.rows.map((row) => (
                                        <InvoiceRow
                                            key={row.OrderRowId}
                                            row={row}
                                            isSelected={selectedRowIds.indexOf(normalizeOrderRowId(row.OrderRowId)) >= 0}
                                            onToggleSelect={onToggleRow}
                                            onStatusChange={onRowStatusChange}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default InvoiceGroupAccordion;
